import { Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { fromEvent, interval, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { assessmentTaskSessionIdentity } from "./assessment_task_session.util";
import { AssessmentTasksService } from "./assessment_tasks.service";
import { LoginService } from "./login.service";

@Injectable({ providedIn: "root" })
export class AssessmentTaskTimezoneObserverService implements OnDestroy {
    private listeners = new Subscription();
    private request: Subscription | null = null;
    private sessionIdentity: string | null = null;
    private requestedTimezone: string | null = null;
    private observedTimezone: string | null = null;
    private savedTimezone: string | null = null;
    private started = false;

    constructor(
        private readonly login: LoginService,
        private readonly tasks: AssessmentTasksService,
        private readonly router: Router
    ) {}

    start(): void {
        if (this.started || typeof window === "undefined" || typeof document === "undefined") return;
        this.started = true;
        this.listeners.add(this.login.getUserUpdates().subscribe((user) => {
            const identity = assessmentTaskSessionIdentity(user);
            if (identity !== this.sessionIdentity) {
                this.request?.unsubscribe();
                this.request = null;
                this.requestedTimezone = null;
                this.observedTimezone = null;
                this.savedTimezone = null;
                this.sessionIdentity = identity;
            }
            this.observe();
        }));
        this.listeners.add(fromEvent(window, "focus").subscribe(() => this.observe()));
        this.listeners.add(fromEvent(document, "visibilitychange").subscribe(() => {
            if (document.visibilityState === "visible") this.observe();
        }));
        this.listeners.add(this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        ).subscribe(() => this.observe()));
        // Focus/navigation cover ordinary use; this also catches an OS timezone change in an active tab.
        this.listeners.add(interval(5 * 60_000).subscribe(() => this.observe()));
    }

    ngOnDestroy(): void {
        this.stop();
    }

    stop(): void {
        this.listeners.unsubscribe();
        this.listeners = new Subscription();
        this.request?.unsubscribe();
        this.request = null;
        this.requestedTimezone = null;
        this.observedTimezone = null;
        this.savedTimezone = null;
        this.sessionIdentity = null;
        this.started = false;
    }

    private observe(): void {
        if (!this.sessionIdentity || !this.login.isSessionValid() || document.visibilityState === "hidden") return;

        let timezone: string;
        try {
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (!timezone || timezone.length > 100 ||
                !/^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+)*$/.test(timezone)) return;
            new Intl.DateTimeFormat("en-US", { timeZone: timezone });
        } catch {
            return;
        }

        this.observedTimezone = timezone;
        this.writeLatestObservation();
    }

    private writeLatestObservation(): void {
        const timezone = this.observedTimezone;
        if (!this.sessionIdentity || !timezone || this.requestedTimezone || timezone === this.savedTimezone) return;

        this.requestedTimezone = timezone;
        const identity = this.sessionIdentity;
        this.request = this.tasks.updateBrowserTimezone(timezone).subscribe({
            next: () => {
                if (this.sessionIdentity === identity && this.requestedTimezone === timezone) {
                    this.savedTimezone = timezone;
                }
            },
            error: () => {
                if (this.sessionIdentity === identity && this.requestedTimezone === timezone) {
                    this.requestedTimezone = null;
                    this.request = null;
                    // Retry a newer observation immediately; retry this failed zone on a later observation.
                    if (this.observedTimezone !== timezone) this.writeLatestObservation();
                }
            },
            complete: () => {
                if (this.sessionIdentity === identity && this.requestedTimezone === timezone) {
                    this.requestedTimezone = null;
                    this.request = null;
                    this.writeLatestObservation();
                }
            }
        });
    }
}
